import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import { Card } from "../../Components/Card";
import { useRef, useState } from "react";
import { useFetch } from "../../hooks/useFetch";
import primeClient from "../../axios/primeClient";
import { timeAgo } from "../../utils/timeAgo";
import hiClient from "../../axios/hiClient";
import { useNavigate } from "react-router-dom";
import { parseDate } from "../../utils/parseDate";
import { useTranslation } from "react-i18next";
import { NetworkIcon } from "../../Components/Icons/NetworkIcon";
import { ServerIcon } from "../../Components/Icons/ServerIcon";
import { WebAPIIcon } from "../../Components/Icons/WebAPIIcon";
import { Colors } from "../../constants/Colors";

const deviceStatusColor = {
  // Mona Hi device status
  "internet-online": Colors.alertsgreen100,
  "internet-offline": Colors.alertsred100,
  "internet-technical-issue": "#a7a9ac",
  "internet-suspend": Colors.alertsyellow100,

  // Mona Prime device status
  "device-clear": Colors.alertsgreen100,
  "device-attention": Colors.alertsyellow100,
  "device-down": Colors.alertsred100,
};

export const DeviceViewAll = ({ deviceStatus }) => {
  const loginType = localStorage.getItem("login_type");
  return (
    <div
      style={{
        padding: "2rem 1rem 0 1rem",
        width: "100%",
        height: "calc(100vh - 62px)",
        overflow: "hidden",
      }}
    >
      <Title deviceStatus={deviceStatus} />
      <Description deviceStatus={deviceStatus} />
      {loginType === "prime" ? (
        <PrimeDeviceDetail deviceStatus={deviceStatus} />
      ) : (
        <HiDeviceDetail deviceStatus={deviceStatus} />
      )}
    </div>
  );
};

const Title = ({ deviceStatus }) => {
  const title = deviceStatus
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  return (
    <div
      style={{
        fontWeight: "bold",
        fontSize: "1.25rem",
      }}
    >
      <span
        style={{
          display: "inline-block",
          width: "1rem",
          height: "1rem",
          borderRadius: "50%",
          backgroundColor: deviceStatusColor[deviceStatus],
          marginRight: "1rem",
        }}
      ></span>
      {title}
    </div>
  );
};

const Description = ({ deviceStatus }) => {
  const { t } = useTranslation();
  const deviceStatusDescription = {
    // Mona Hi device status
    "internet-online": t("hi.deviceCondition.desc.online"),
    "internet-offline": t("hi.deviceCondition.desc.offline"),
    "internet-technical-issue": t("hi.deviceCondition.desc.techIssue"),
    "internet-suspend": t("hi.deviceCondition.desc.suspend"),
    // Mona Prime device status
    "device-clear": t("prime.deviceCondition.desc.clear"),
    "device-attention": t("prime.deviceCondition.desc.attention"),
    "device-down": t("prime.deviceCondition.desc.down"),
  };
  return (
    <Card
      style={{
        marginTop: "0.5rem",
        marginBottom: "0.5rem",
        padding: "10px",
        borderRadius: "10px",
        backgroundColor: "#ffffff",
      }}
    >
      {deviceStatusDescription[deviceStatus]}
    </Card>
  );
};

const PrimeDeviceDetail = ({ deviceStatus }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const inputRef = useRef();

  const { t, i18n } = useTranslation();

  const handleSearch = () => {
    setSearchQuery(inputRef.current.value);
  };

  const trimmedDeviceStatus =
    deviceStatus === "device-clear" ? "up" : deviceStatus.split("-")[1];

  const { data: deviceStatusResponse } = useFetch({
    url: `/v1/apps/list?status=${trimmedDeviceStatus}`,
    client: primeClient,
    initialData: { device: [] },
  });

  const dataMapper = (el) => ({
    id:
      el.type === "network"
        ? el.network_id
        : el.type === "server"
        ? el.server_id
        : el.web_id,
    name: el.name,
    identifier: el.ip_address,
    lastUpdated: timeAgo(el.last_update, t),
    type: el.type,
    description: [
      ...el.root_cause.component.critical.map((comp) => {
        return i18n.language === "id"
          ? `Kapasitas ${comp.replaceAll("_", " ")} perangkat kritis (>80%)`
          : `Device ${comp.replaceAll("_", " ")} capacity is critical (>80%)`;
      }),
      ...el.root_cause.component.warning.map((comp) => {
        return i18n.language === "id"
          ? `Kapasitas ${comp.replaceAll("_", " ")} perangkat warning (>60%)`
          : `Device ${comp.replaceAll("_", " ")} capacity is warning (>60%)`;
      }),
      ...el.root_cause.network.poor.map((comp) => {
        return i18n.language === "id"
          ? `Kualitas ${comp.replaceAll("_", " ")} perangkat buruk`
          : `Device ${comp.replaceAll("_", " ")} is poor`;
      }),
      ...(deviceStatus === "device-down"
        ? [t("prime.deviceList.deviceOff")]
        : []),
    ],
    last_update: el.last_update,
  });

  const sortByLastUpdated = (data) =>
    [...data].sort(
      (a, b) => parseDate(b.last_update) - parseDate(a.last_update)
    );

  return (
    <Card
      style={{
        marginTop: "0.5rem",
        marginBottom: "0.5rem",
        padding: "10px",
        borderRadius: "10px",
        backgroundColor: "#ffffff",
        height: "80%",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          // backgroundColor: "lightgray",
          padding: "10px",
          borderRadius: "40px",
          color: "#6a6a6a",
          borderBlockStyle: "solid",
          borderWidth: "1.5px",
          borderColor: "#bbb",
        }}
      >
        <MagnifyingGlassIcon
          width={"1.4rem"}
          height={"1.4rem"}
          style={{ cursor: "pointer", marginRight: "10px" }}
          onClick={handleSearch}
        />
        <input
          type="text"
          placeholder={t("hi.deviceCondition.search.placeholder")}
          ref={inputRef}
          style={{
            backgroundColor: "transparent",
            border: "none",
            outline: "none",
            color: "#6a6a6a",
            flex: "1",
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSearch();
          }}
        />
      </div>
      {sortByLastUpdated(deviceStatusResponse.device.map(dataMapper)).length >
      0 ? (
        <div
          style={{
            gap: "5px",
            marginTop: "1rem",
            overflowX: "hidden",
            overflowY: "scroll",
            flexGrow: "1",
            height: "80%",
          }}
        >
          {sortByLastUpdated(deviceStatusResponse.device.map(dataMapper))
            .filter(
              (el) =>
                searchQuery.trim() === "" ||
                el.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                el.identifier?.toLowerCase().includes(searchQuery.toLowerCase())
            )
            .map((el, idx) => (
              <CardSubItem deviceStatus={deviceStatus} subItem={el} key={idx} />
            ))}
        </div>
      ) : (
        <div style={{ padding: "1rem", textAlign: "center", color: "black" }}>
          {t("general.noData")}
        </div>
      )}
    </Card>
  );
};

const HiDeviceDetail = ({ deviceStatus }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const inputRef = useRef();
  const { t } = useTranslation();

  const handleSearch = () => {
    setSearchQuery(inputRef.current.value);
  };
  const { data: deviceStatusResponse } = useFetch({
    url: "/mobile/internet/list?status_internet=ALL",
    client: hiClient,
    initialData: {
      online_internet: [],
      dying_internet: [],
      offline_internet: [],
      los_internet: [],
      unknown_internet: [],
      sync_internet: [],
      suspend: [],
    },
  });

  const dataMapper = (description) => (el) => ({
    name: el.Name,
    id: el.id,
    identifier: el.masked_nd,
    lastUpdated: timeAgo(el.updated_at, t),
    description: [description],
    last_update: el.updated_at,
  });

  const sortByLastUpdated = (data) =>
    [...data].sort(
      (a, b) => parseDate(b.last_update) - parseDate(a.last_update)
    );

  const renderCardSubItems = () => {
    let data = [];
    if (deviceStatus === "internet-online") {
      data = [
        ...sortByLastUpdated(
          deviceStatusResponse.online_internet.map(
            dataMapper("Internet Online")
          )
        ),
        ...sortByLastUpdated(
          deviceStatusResponse.dying_internet.map(
            dataMapper("Internet Dying Gasp")
          )
        ),
      ];
    } else if (deviceStatus === "internet-offline") {
      data = [
        ...sortByLastUpdated(
          deviceStatusResponse.offline_internet.map(
            dataMapper("Internet Offline")
          )
        ),
        ...sortByLastUpdated(
          deviceStatusResponse.los_internet.map(dataMapper("Internet LOS"))
        ),
      ];
    } else if (deviceStatus === "internet-technical-issue") {
      data = [
        ...sortByLastUpdated(
          deviceStatusResponse.unknown_internet.map(
            dataMapper("Internet Unknown")
          )
        ),
        ...sortByLastUpdated(
          deviceStatusResponse.sync_internet.map(dataMapper("Internet Sync"))
        ),
      ];
    } else {
      data = sortByLastUpdated(
        deviceStatusResponse.suspend.map(dataMapper("Internet Suspend"))
      );
    }

    return data
      .filter(
        (el) =>
          searchQuery.trim() === "" ||
          el.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          el.identifier?.toLowerCase().includes(searchQuery.toLowerCase())
      )
      .map((el, idx) => (
        <CardSubItem deviceStatus={deviceStatus} subItem={el} key={idx} />
      ));
  };

  return (
    <Card
      style={{
        marginTop: "0.5rem",
        marginBottom: "0.5rem",
        padding: "10px",
        borderRadius: "10px",
        backgroundColor: "#ffffff",
        height: "80%",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          // backgroundColor: "lightgray",
          padding: "10px",
          borderRadius: "40px",
          color: "#6a6a6a",
          borderBlockStyle: "solid",
          borderWidth: "1.5px",
          borderColor: "#bbb",
        }}
      >
        <MagnifyingGlassIcon
          width={"1.4rem"}
          height={"1.4rem"}
          style={{ cursor: "pointer", marginRight: "10px" }}
          onClick={handleSearch}
        />
        <input
          type="text"
          placeholder={t("hi.deviceCondition.search.placeholder")}
          ref={inputRef}
          style={{
            backgroundColor: "transparent",
            border: "none",
            outline: "none",
            color: "#6a6a6a",
            flex: "1",
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSearch();
          }}
        />
      </div>
      <div
        style={{
          gap: "5px",
          marginTop: "1rem",
          overflowX: "hidden",
          overflowY: "scroll",
          flexGrow: "1",
          height: "80%",
        }}
      >
        {renderCardSubItems()}
      </div>
    </Card>
  );
};

const CardSubItem = ({
  deviceStatus,
  subItem: { name, identifier, lastUpdated, description, id, type },
}) => {
  return (
    <div
      style={{
        display: "flex",
        gap: "0.5rem",
        width: "100%",
        margin: "8px",
        marginBottom: "0",
        flexGrow: "1",
        borderLeft: `5px solid ${deviceStatusColor[deviceStatus]}`,
        paddingLeft: "0.4rem",
        cursor: "pointer",
      }}
      onClick={() =>
        window.location.replace(
          `/analytics/device-detail-analysis/${type}/${id}`
        )
      }
    >
      <div
        style={{
          height: "100%",
          alignItems: "center",
          alignContent: "center",
          alignSelf: "center",
        }}
      >
        {type === "server" && (
          <ServerIcon
            style={{
              width: "20px",
              height: "20px",
              alignItems: "center",
              alignContent: "center",
              alignSelf: "center",
            }}
            isBlack={true}
          />
        )}
        {type === "network" && (
          <NetworkIcon
            style={{ width: "20px", height: "20px" }}
            isBlack={true}
          />
        )}
        {type === "webapi" && (
          <WebAPIIcon
            style={{ width: "20px", height: "20px" }}
            isBlack={true}
          />
        )}
      </div>
      <div
        style={{
          flexGrow: "1",
          fontSize: "0.8rem",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "95%",
            fontWeight: "bold",
          }}
        >
          <div>{name}</div>
          <div style={{ fontWeight: 500 }}>{lastUpdated}</div>
        </div>
        <div style={{ color: "#525252" }}>{identifier}</div>
        <div>
          {description.map((el) => (
            <>
              {el}
              <br />
            </>
          ))}
        </div>
      </div>
    </div>
  );
};
